import { motion } from 'framer-motion';
import SEO from '@/components/SEO';

const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5 } }
};

const Section = ({ title, children }) => (
  <motion.div
    initial="hidden"
    whileInView="visible"
    viewport={{ once: true }}
    variants={fadeInUp}
    className="mb-10"
  >
    <h2 className="font-playfair text-2xl text-navy mb-4">{title}</h2>
    <div className="text-mid-grey leading-relaxed space-y-3">{children}</div>
  </motion.div>
);

const TermsOfUsePage = () => {
  return (
    <div className="pt-20" data-testid="terms-of-use-page">
      <SEO
        title="Terms of Use"
        description="The terms and conditions governing your use of the H&G (Horizon & Greenprint) website, hng.world."
        path="/terms-of-use"
      />

      {/* Hero */}
      <section className="py-16 md:py-20 bg-gradient-to-br from-white via-light-grey to-white">
        <div className="max-w-4xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <h1 className="font-playfair text-4xl md:text-5xl text-navy mb-4">
              Terms of Use
            </h1>
            <p className="text-lg text-mid-grey">
              Effective date: August 10, 2026
            </p>
          </motion.div>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 md:py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6">

          <Section title="1. Acceptance of Terms">
            <p>
              These Terms of Use (&ldquo;Terms&rdquo;) govern your access to and use of hng.world (the
              &ldquo;Website&rdquo;), operated by H&amp;G (Horizon &amp; Greenprint) (&ldquo;H&amp;G&rdquo;, &ldquo;we&rdquo;,
              &ldquo;us&rdquo;, &ldquo;our&rdquo;). By accessing or using the Website, you agree to be bound by these
              Terms. If you do not agree, please do not use the Website.
            </p>
          </Section>

          <Section title="2. About This Website">
            <p>
              The Website provides information about H&amp;G&rsquo;s advisory and engineering services across
              pharmaceutical engineering, sustainability, finance, capital markets, and M&amp;A, and allows
              visitors to submit enquiries through our Contact form. The Website is informational in nature and
              does not itself provide any professional advisory service &mdash; any engagement with H&amp;G is
              governed by a separate agreement executed between the parties.
            </p>
          </Section>

          <Section title="3. Use of the Website">
            <p>You agree to use the Website only for lawful purposes. You agree not to:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Use the Website in any way that could damage, disable, overburden, or impair it</li>
              <li>Attempt to gain unauthorized access to any part of the Website, other accounts, or connected systems</li>
              <li>Submit false, misleading, or fraudulent information through the Contact form</li>
              <li>Use any automated system (bot, scraper, crawler) to access the Website except standard search engine indexing</li>
              <li>Reproduce, copy, or exploit any portion of the Website for commercial purposes without our written consent</li>
            </ul>
          </Section>

          <Section title="4. Intellectual Property">
            <p>
              All content on the Website &mdash; including text, graphics, logos, the H&amp;G name and branding,
              and the underlying design and code &mdash; is owned by or licensed to H&amp;G and is protected by
              applicable intellectual property laws. You may view and print pages of the Website for your own
              personal, non-commercial reference. No other use, including reproduction, retransmission, or
              modification, is permitted without our prior written consent.
            </p>
          </Section>

          <Section title="5. No Professional Advice">
            <p>
              Content on the Website &mdash; including service descriptions, insights, and case studies &mdash;
              is provided for general informational purposes only and does not constitute engineering,
              regulatory, financial, legal, or other professional advice. You should not act, or refrain from
              acting, on the basis of any content on the Website without seeking appropriate professional advice
              specific to your circumstances through a formal engagement with H&amp;G.
            </p>
          </Section>

          <Section title="6. Third-Party Links and Services">
            <p>
              The Website may reference or rely on third-party services, including Cloudflare (for spam
              protection on our Contact form) and email delivery providers. We are not responsible for the
              content, accuracy, or practices of third-party services, and your use of them may be subject to
              their own terms and privacy policies.
            </p>
          </Section>

          <Section title="7. Disclaimer of Warranties">
            <p>
              The Website is provided on an &ldquo;as is&rdquo; and &ldquo;as available&rdquo; basis. While we
              take reasonable care to keep information on the Website accurate and up to date, we make no
              warranties, express or implied, as to the accuracy, completeness, or reliability of any content,
              and we do not warrant that the Website will be uninterrupted or error-free.
            </p>
          </Section>

          <Section title="8. Limitation of Liability">
            <p>
              To the fullest extent permitted by law, H&amp;G shall not be liable for any direct, indirect,
              incidental, or consequential loss or damage arising from your access to, use of, or inability to
              use the Website, including reliance on any content published on it. Nothing in these Terms limits
              our liability where it cannot be limited under applicable law.
            </p>
          </Section>

          <Section title="9. Changes to These Terms">
            <p>
              We may revise these Terms from time to time. The &ldquo;Effective date&rdquo; above reflects when
              they were last updated. Continued use of the Website after changes are posted constitutes your
              acceptance of the revised Terms.
            </p>
          </Section>

          <Section title="10. Governing Law">
            <p>
              These Terms are governed by the laws of India. Any disputes arising from these Terms or your use
              of the Website shall be subject to the exclusive jurisdiction of the courts having competence over
              our registered place of business in India.
            </p>
          </Section>

          <Section title="11. Contact Us">
            <p>
              For any questions about these Terms, write to us at{' '}
              <a href="mailto:contact@hng.world" className="text-teal hover:underline">contact@hng.world</a>.
              For questions about how we handle your personal data, see our{' '}
              <a href="/en-in/privacy-policy" className="text-teal hover:underline">Privacy Policy</a>.
            </p>
          </Section>

        </div>
      </section>
    </div>
  );
};

export default TermsOfUsePage;
